import { useRef } from 'react';
import { useCanvas } from '../state/canvas-store.js';
import { downloadJSON, fromExcalidrawFile, readFile, toExcalidrawFile } from '../lib/persistence.js';
import { exportToPNG } from '../lib/export-image.js';
import { exportToSVG } from '../lib/export-svg.js';
import type { ExcalidrawFile } from '@extapia/shared';

interface Props {
  onOpenShare: () => void;
  onOpenTemplates: () => void;
  onOpenAuth: () => void;
  onOpenHelp: () => void;
}

export const TopBar = ({ onOpenShare, onOpenTemplates, onOpenAuth, onOpenHelp }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const undo = useCanvas((s) => s.undo);
  const redo = useCanvas((s) => s.redo);
  const app = useCanvas((s) => s.app);
  const theme = app.theme;
  const setTheme = useCanvas((s) => s.setTheme);
  const setGrid = useCanvas((s) => s.setGrid);
  const setViewport = useCanvas((s) => s.setViewport);
  const toJSON = useCanvas((s) => s.toJSON);
  const loadFromJSON = useCanvas((s) => s.loadFromJSON);
  const elements = useCanvas((s) => s.elements);

  const handleExportJSON = () => {
    const snap = toJSON();
    downloadJSON(toExcalidrawFile(snap), `excalidraw-extapia-${Date.now()}.excalidraw`);
  };
  const handleExportPNG = () => exportToPNG(elements, app.viewBackgroundColor);
  const handleExportSVG = () => exportToSVG(elements, app.viewBackgroundColor);

  const handleImport = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const text = await readFile(file);
      const data = JSON.parse(text) as ExcalidrawFile;
      if (data.type !== 'excalidraw') return alert('File bukan .excalidraw yang valid');
      loadFromJSON(fromExcalidrawFile(data));
    } catch (err) {
      console.error(err);
      alert('Gagal membaca file');
    } finally {
      ev.target.value = '';
    }
  };

  const zoomIn = () => setViewport({ zoom: Math.min(30, app.zoom * 1.2) });
  const zoomOut = () => setViewport({ zoom: Math.max(0.1, app.zoom / 1.2) });
  const zoomReset = () => setViewport({ zoom: 1, scrollX: 0, scrollY: 0 });

  const btn =
    'px-2.5 h-8 rounded-md text-xs font-medium bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700 transition-colors';
  const primary = 'px-3 h-8 rounded-md text-xs font-semibold bg-sky-500 text-white hover:bg-sky-600 transition-colors';

  return (
    <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 flex-wrap max-w-[calc(100vw-2rem)] justify-end">
      <button className={btn} onClick={undo} title="Undo (Ctrl+Z)">↶</button>
      <button className={btn} onClick={redo} title="Redo (Ctrl+Y)">↷</button>
      <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-700 mx-1" />
      <button className={btn} onClick={zoomOut}>−</button>
      <button className={btn} onClick={zoomReset} title="Reset zoom">{Math.round(app.zoom * 100)}%</button>
      <button className={btn} onClick={zoomIn}>+</button>
      <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-700 mx-1" />
      <button className={btn} onClick={() => setGrid(app.gridSize ? null : 20)}>
        {app.gridSize ? 'Grid on' : 'Grid off'}
      </button>
      <button className={btn} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
        {theme === 'dark' ? '☾' : '☀'}
      </button>
      <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-700 mx-1" />
      <button className={btn} onClick={onOpenTemplates}>Templates</button>
      <button className={btn} onClick={() => fileInputRef.current?.click()}>Import</button>
      <input ref={fileInputRef} type="file" accept=".excalidraw,application/json" hidden onChange={handleImport} />
      <button className={btn} onClick={handleExportJSON}>JSON</button>
      <button className={btn} onClick={handleExportPNG}>PNG</button>
      <button className={btn} onClick={handleExportSVG}>SVG</button>
      <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-700 mx-1" />
      <button className={btn} onClick={onOpenHelp} title="Shortcuts (?)">?</button>
      <button className={btn} onClick={onOpenAuth}>Sign in</button>
      <button className={primary} onClick={onOpenShare}>Share</button>
    </div>
  );
};
